<script setup>
import { computed, onUnmounted, ref, nextTick, watch } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { debounce } from '@wasel/utils';
import { useAccount } from 'dashboard/composables/useAccount';
import MyAiResponseAPI from 'dashboard/api/my_ai/response';

import Banner from 'dashboard/components-next/banner/Banner.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import BulkSelectBar from 'dashboard/components-next/my_ai/assistant/BulkSelectBar.vue';
import DeleteDialog from 'dashboard/components-next/my_ai/pageComponents/DeleteDialog.vue';
import BulkDeleteDialog from 'dashboard/components-next/my_ai/pageComponents/BulkDeleteDialog.vue';
import PageLayout from 'dashboard/components-next/my_ai/PageLayout.vue';
import ResponseCard from 'dashboard/components-next/my_ai/assistant/ResponseCard.vue';
import CreateResponseDialog from 'dashboard/components-next/my_ai/pageComponents/response/CreateResponseDialog.vue';
import ResponsePageEmptyState from 'dashboard/components-next/my_ai/pageComponents/emptyStates/ResponsePageEmptyState.vue';
import FeatureSpotlightPopover from 'dashboard/components-next/feature-spotlight/FeatureSpotlightPopover.vue';
import LimitBanner from 'dashboard/components-next/my_ai/pageComponents/response/LimitBanner.vue';
import ConversationUsageDrawer from 'dashboard/components-next/my_ai/pageComponents/ConversationUsageDrawer.vue';
import { labels as knowledgeMessages } from '../knowledge/labels';

const router = useRouter();
const route = useRoute();
const store = useStore();
const { t, locale } = useI18n();
const { isOnWaselCloud } = useAccount();
const uiFlags = useMapGetter('myAiResponses/getUIFlags');
const responseMeta = useMapGetter('myAiResponses/getMeta');
const responses = useMapGetter('myAiResponses/getRecords');
const isFetching = computed(() => uiFlags.value.fetchingList);
const knowledgeBacked = ref(false);
const knowledgeLabels = computed(
  () => knowledgeMessages[locale.value?.startsWith('ar') ? 'ar' : 'en']
);

const selectedResponse = ref(null);
const usageResponse = ref(null);
const showResponseUsage = ref(false);
const deleteDialog = ref(null);
const bulkDeleteDialog = ref(null);

const dialogType = ref('');
const searchQuery = ref('');

const createDialog = ref(null);

const selectedAssistantId = computed(() => Number(route.params.assistantId));

const suggestionCount = useMapGetter('myAiFaqSuggestions/getOpenCount');

const handleDelete = () => {
  deleteDialog.value.dialogRef.open();
};

const handleCreate = () => {
  if (isFetching.value) return;
  if (knowledgeBacked.value) {
    router.push({
      name: 'my_ai_assistants_knowledge_index',
      params: route.params,
      query: { create: 'faq' },
    });
    return;
  }
  dialogType.value = 'create';
  nextTick(() => createDialog.value.dialogRef.open());
};

const handleEdit = () => {
  dialogType.value = 'edit';
  nextTick(() => createDialog.value.dialogRef.open());
};

const handleAction = ({ action, id }) => {
  selectedResponse.value = responses.value.find(response => id === response.id);
  if (knowledgeBacked.value) {
    router.push({
      name: 'my_ai_assistants_knowledge_index',
      params: route.params,
      query: { record: selectedResponse.value.record_id },
    });
    return;
  }
  nextTick(() => {
    if (action === 'delete') {
      handleDelete();
    }
    if (action === 'edit') {
      handleEdit();
    }
  });
};

const handleNavigationAction = ({ id, type }) => {
  if (type === 'Conversation') {
    router.push({
      name: 'inbox_conversation',
      params: { conversation_id: id },
    });
  }
};

const handleCreateClose = () => {
  dialogType.value = '';
  selectedResponse.value = null;
};

const handleShowResponseUsage = id => {
  usageResponse.value =
    responses.value.find(response => response.id === id) || null;
  showResponseUsage.value = Boolean(usageResponse.value);
};

const handleResponseUsageClose = () => {
  showResponseUsage.value = false;
};

const fetchResponseUsage = ({ resourceId, ...params }) =>
  MyAiResponseAPI.getDrilldown({ responseId: resourceId, ...params });

const updateURLWithFilters = (page, search) => {
  const query = {
    page: page || 1,
  };

  if (search) {
    query.search = search;
  }

  router.replace({ query });
};

const { run: runListRequest, abort: abortListRequest } = useAbortableRequest();

const fetchResponses = async (page = 1) => {
  const filterParams = { page };

  if (selectedAssistantId.value) {
    filterParams.assistantId = selectedAssistantId.value;
  }
  if (searchQuery.value) {
    filterParams.search = searchQuery.value;
  }

  // Update URL with current filters
  updateURLWithFilters(page, searchQuery.value);

  store.dispatch('myAiResponses/setFetchingList', true);

  try {
    const response = await runListRequest(signal =>
      MyAiResponseAPI.get({ ...filterParams, signal })
    );

    if (!response) return;

    knowledgeBacked.value = response.data.meta.knowledge_base === true;

    store.dispatch('myAiResponses/setRecords', {
      records: response.data.payload,
      meta: response.data.meta,
    });
    store.dispatch('myAiResponses/setFetchingList', false);
  } catch (error) {
    useAlert(error?.message || t('MY_AI.RESPONSES.ERRORS.LOAD'));
    store.dispatch('myAiResponses/setFetchingList', false);
  }
};

// Bulk action
const bulkSelectedIds = ref(new Set());
const hoveredCard = ref(null);

const buildSelectedCountLabel = computed(() => {
  const count = responses.value?.length || 0;
  const isAllSelected = bulkSelectedIds.value.size === count && count > 0;
  return isAllSelected
    ? t('MY_AI.RESPONSES.UNSELECT_ALL', { count })
    : t('MY_AI.RESPONSES.SELECT_ALL', { count });
});

const selectedCountLabel = computed(() => {
  return t('MY_AI.RESPONSES.SELECTED', {
    count: bulkSelectedIds.value.size,
  });
});

const handleCardHover = (isHovered, id) => {
  hoveredCard.value = isHovered ? id : null;
};

const handleCardSelect = id => {
  const selected = new Set(bulkSelectedIds.value);
  selected[selected.has(id) ? 'delete' : 'add'](id);
  bulkSelectedIds.value = selected;
};

const fetchResponseAfterBulkAction = () => {
  const hasNoResponsesLeft = responses.value?.length === 0;
  const currentPage = responseMeta.value?.page;

  if (hasNoResponsesLeft) {
    // Page is now empty after bulk action.
    // Fetch the previous page if not already on the first page.
    const pageToFetch = currentPage > 1 ? currentPage - 1 : currentPage;
    fetchResponses(pageToFetch);
  } else {
    // Page still has responses left, re-fetch the same page.
    fetchResponses(currentPage);
  }

  // Clear selection
  bulkSelectedIds.value = new Set();
};

const onPageChange = page => {
  const hadSelection = bulkSelectedIds.value.size > 0;

  showResponseUsage.value = false;
  usageResponse.value = null;

  fetchResponses(page);

  if (hadSelection) {
    bulkSelectedIds.value = new Set();
  }
};

const onDeleteSuccess = () => {
  if (responses.value?.length === 0 && responseMeta.value?.page > 1) {
    onPageChange(responseMeta.value.page - 1);
  }
};

const onBulkDeleteSuccess = () => {
  fetchResponseAfterBulkAction();
};

const debouncedSearch = debounce(async () => {
  fetchResponses(1);
}, 500);

const handleSearchInput = () => {
  abortListRequest();
  debouncedSearch();
};

const initializeFromURL = () => {
  searchQuery.value = route.query.search || '';
  const pageFromURL = parseInt(route.query.page, 10) || 1;
  fetchResponses(pageFromURL);
};

const navigateToFaqSuggestions = () => {
  router.push({
    name: 'my_ai_assistants_faq_suggestions',
    params: {
      accountId: route.params.accountId,
      assistantId: selectedAssistantId.value,
    },
  });
};

watch(
  selectedAssistantId,
  () => {
    knowledgeBacked.value = false;
    dialogType.value = '';
    selectedResponse.value = null;
    usageResponse.value = null;
    showResponseUsage.value = false;
    bulkSelectedIds.value = new Set();
    store.dispatch('myAiResponses/setRecords', {
      records: [],
      meta: { page: 1, total_count: 0 },
    });
    initializeFromURL();
    store.dispatch(
      'myAiFaqSuggestions/fetchOpenCount',
      selectedAssistantId.value
    );
  },
  { immediate: true }
);

onUnmounted(() => {
  store.dispatch('myAiResponses/setFetchingList', false);
});
</script>

<template>
  <PageLayout
    :total-count="responseMeta.totalCount"
    :current-page="responseMeta.page"
    :button-policy="['administrator']"
    :header-title="$t('MY_AI.RESPONSES.HEADER')"
    :button-label="
      knowledgeBacked
        ? knowledgeLabels.createFaq
        : $t('MY_AI.RESPONSES.ADD_NEW')
    "
    :show-know-more="!knowledgeBacked"
    :is-fetching="isFetching"
    :is-empty="!responses.length"
    :show-pagination-footer="!isFetching && !!responses.length"
    @update:current-page="onPageChange"
    @click="handleCreate"
  >
    <template #knowMore>
      <FeatureSpotlightPopover
        :button-label="$t('MY_AI.HEADER_KNOW_MORE')"
        :title="$t('MY_AI.RESPONSES.EMPTY_STATE.FEATURE_SPOTLIGHT.TITLE')"
        :note="$t('MY_AI.RESPONSES.EMPTY_STATE.FEATURE_SPOTLIGHT.NOTE')"
        :hide-actions="!isOnWaselCloud"
        fallback-thumbnail="/assets/images/dashboard/my_ai/faqs-popover-light.svg"
        fallback-thumbnail-dark="/assets/images/dashboard/my_ai/faqs-popover-dark.svg"
      />
    </template>

    <template #search>
      <div
        v-if="bulkSelectedIds.size === 0"
        class="flex gap-3 justify-between w-full items-center"
      >
        <Input
          v-model="searchQuery"
          :placeholder="$t('MY_AI.RESPONSES.SEARCH_PLACEHOLDER')"
          class="w-64"
          size="sm"
          type="search"
          autofocus
          @input="handleSearchInput"
        />
      </div>
    </template>

    <template #subHeader>
      <BulkSelectBar
        v-if="!knowledgeBacked"
        v-model="bulkSelectedIds"
        :all-items="responses"
        :select-all-label="buildSelectedCountLabel"
        :selected-count-label="selectedCountLabel"
        :delete-label="$t('MY_AI.RESPONSES.BULK_DELETE_BUTTON')"
        class="w-fit"
        :class="{
          'mb-2': bulkSelectedIds.size > 0,
        }"
        @bulk-delete="bulkDeleteDialog.dialogRef.open()"
      />
    </template>

    <template #emptyState>
      <p v-if="knowledgeBacked" class="p-8 text-center text-n-slate-11">
        {{ knowledgeLabels.faqEmpty }}
      </p>
      <ResponsePageEmptyState v-else @click="handleCreate" />
    </template>

    <template #body>
      <Banner v-if="knowledgeBacked" color="blue" class="mb-5">
        {{ knowledgeLabels.faqPublished }}
      </Banner>
      <LimitBanner v-else class="mb-5" />
      <Banner
        v-if="!knowledgeBacked && suggestionCount > 0"
        color="blue"
        class="mb-4 -mt-3"
        :action-label="$t('MY_AI.RESPONSES.SUGGESTIONS_BANNER.ACTION')"
        @action="navigateToFaqSuggestions"
      >
        {{ $t('MY_AI.RESPONSES.SUGGESTIONS_BANNER.TITLE') }}
      </Banner>

      <div class="flex flex-col gap-4">
        <ResponseCard
          v-for="response in responses"
          :id="response.id"
          :key="response.id"
          :question="response.question"
          :answer="response.answer"
          :assistant="response.assistant"
          :documentable="response.documentable"
          :status="response.status"
          :created-at="response.created_at"
          :updated-at="response.updated_at"
          :used-in-conversations-count="response.used_in_conversations_count"
          :is-selected="bulkSelectedIds.has(response.id)"
          :selectable="
            !knowledgeBacked &&
            (hoveredCard === response.id || bulkSelectedIds.size > 0)
          "
          :review-label="knowledgeBacked ? knowledgeLabels.title : ''"
          :show-menu="!bulkSelectedIds.has(response.id)"
          :show-actions="false"
          @action="handleAction"
          @navigate="handleNavigationAction"
          @select="handleCardSelect"
          @hover="isHovered => handleCardHover(isHovered, response.id)"
          @view-conversations="handleShowResponseUsage"
        />
      </div>
    </template>

    <ConversationUsageDrawer
      :open="showResponseUsage"
      :resource-id="usageResponse?.id"
      :title="usageResponse?.question || ''"
      :conversation-count="usageResponse?.used_in_conversations_count || 0"
      :fetcher="fetchResponseUsage"
      empty-state-key="MY_AI.RESPONSES.NO_USED_CONVERSATIONS"
      @close="handleResponseUsageClose"
    />

    <DeleteDialog
      v-if="selectedResponse"
      ref="deleteDialog"
      :entity="selectedResponse"
      type="Responses"
      @delete-success="onDeleteSuccess"
    />

    <BulkDeleteDialog
      v-if="!knowledgeBacked && bulkSelectedIds"
      ref="bulkDeleteDialog"
      :bulk-ids="bulkSelectedIds"
      type="AssistantResponse"
      @delete-success="onBulkDeleteSuccess"
    />

    <CreateResponseDialog
      v-if="dialogType"
      ref="createDialog"
      :type="dialogType"
      :selected-response="selectedResponse"
      @close="handleCreateClose"
    />
  </PageLayout>
</template>
